/**
 * File: assets/collection-filter-manager.js
 * Centralized logic for collection filtering (Cards, Desktop, Mobile Drawer)
 * 
 * HYBRID SYSTEM:
 * - Card Filters: Use tag-based URL paths (e.g. /collections/all/sommier)
 * - Sidebar/Drawer Filters: Use query parameters (e.g. ?filter.v.price.gte=10)
 * 
 * REFACTORED ARCHITECTURE:
 * - #FilterDrawerTrigger button is OUTSIDE #CollectionFiltersContainer
 * - Button persists across AJAX updates (no rebinding needed)
 * - Server-rendered counts are preserved (no JS override)
 */

const CollectionFilterManager = {
  config: {
    gridContainerSelector: '#CollectionAjaxContent',
    paginationLinkSelector: '.pagination a',
    filterFormSelector: '#CollectionFiltersForm', // Desktop form
    drawerSelector: '#FilterDrawer', // Mobile drawer
    drawerFormSelector: '#FilterDrawer .filter-form', // Mobile form inside drawer (if exists)
    cardSelector: '.category-filter-card',
    mobileTriggerSelector: '#FilterDrawerTrigger',
    desktopFilterContainer: '#CollectionFiltersContainer',
    mobileFilterContainer: '#FilterDrawer .drawer__scrollable', // FIXED: More specific selector
    sectionId: 'CollectionSection', // Default, might need dynamic update
  },

  state: {
    isLoading: false,
    currentParams: new URLSearchParams(window.location.search),
    // Helper to get active tag from path
    getActiveTags() {
        const path = window.location.pathname;
        const parts = path.split('/');
        const collectionIndex = parts.indexOf('collections');
        if (collectionIndex >= 0 && parts[collectionIndex + 2]) {
            return parts[collectionIndex + 2].split('+')
                .map(t => decodeURIComponent(t).toLowerCase())
                .filter(t => t.length > 0);
        }
        return [];
    },
    getCollectionBaseUrl() {
        const path = window.location.pathname;
        const parts = path.split('/');
        const collectionIndex = parts.indexOf('collections');
        if (collectionIndex >= 0 && parts[collectionIndex + 1]) {
            return `/collections/${parts[collectionIndex + 1]}`;
        }
        return '/collections/all';
    }
  },

  init() {
    console.log('🚀 CollectionFilterManager.init() called');
    this.state.currentParams = new URLSearchParams(window.location.search);
    
    console.log('📋 Initial params:', this.state.currentParams.toString());
    
    this.bindEvents();
    console.log('✅ Events bound');
    
    this.ensurePriceRangeVisible();
    this.hijackDrawerInputs();
    this.syncUI();
    
    console.log('✅ CollectionFilterManager initialization complete');
    
    // Initialize drawer button - with retry if theme isn't ready yet
    this.initDrawerButton();
    
    if (typeof theme === 'undefined' || !theme.Drawers) {
      setTimeout(() => this.initDrawerButton(), 100);
      setTimeout(() => this.initDrawerButton(), 500);
      setTimeout(() => this.initDrawerButton(), 1000);
    }
    
    // Handle browser back/forward
    window.addEventListener('popstate', () => {
      this.state.currentParams = new URLSearchParams(window.location.search);
      this.fetchProducts(window.location.href, false);
      this.syncUI();
    });
  },

  bindEvents() {
    // 1. Event Delegation for Desktop & Drawer Inputs
    document.addEventListener('change', (e) => {
      console.log('📡 Change event detected at document level:', {
        tagName: e.target.tagName,
        type: e.target.type,
        id: e.target.id,
        name: e.target.name,
        classList: e.target.classList.toString(),
        bubbles: e.bubbles
      });
      
      // Check if target is an INPUT and is inside a filter form
      // FIX: matches() doesn't support descendant selectors, so we check tagName first
      const isInput = e.target.tagName === 'INPUT';
      const isInDesktopForm = e.target.closest('#CollectionFiltersForm');
      const isInDrawer = e.target.closest('#FilterDrawer');
      const isSortSelect = e.target.id === 'SortBy';
      
      console.log('🔍 Checking conditions:', {
        isInput,
        isInDesktopForm: !!isInDesktopForm,
        isInDrawer: !!isInDrawer,
        isSortSelect
      });
      
      if (isInput && (isInDesktopForm || isInDrawer)) {
        console.log('✅ Matched filter input - Desktop:', !!isInDesktopForm, 'Drawer:', !!isInDrawer);
        this.handleInputChange(e);
      } else if (isSortSelect) {
        console.log('✅ Matched sort select');
        this.handleSortChange(e);
      } else {
        console.log('❌ Did not match filter input selector');
      }
    });

    // 1b. Guard against native form submission (Enter key on price inputs, etc.)
    // Keep the collection fully AJAX-driven and enforce page reset.
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form) return;

      const isDesktopFilterForm = form.matches(this.config.filterFormSelector);
      const isDrawerFilterForm = form.closest(this.config.drawerSelector);

      if (isDesktopFilterForm || isDrawerFilterForm) {
        e.preventDefault();
        this.resetPaginationParam();
        this.scheduleUpdate();
      }
    });

    // 2. Card Filters
    document.addEventListener('click', (e) => {
      const paginationLink = e.target.closest(this.config.paginationLinkSelector);
      const isGridPaginationLink = paginationLink && paginationLink.closest(this.config.gridContainerSelector);
      if (isGridPaginationLink) {
        e.preventDefault();
        this.handlePaginationClick(paginationLink);
        return;
      }

      const card = e.target.closest(this.config.cardSelector);
      if (card) {
        e.preventDefault();
        this.handleCardClick(card);
      }
      
      // Handle "Clear all" or specific clear buttons
      if (e.target.matches('.js-reset-group') || e.target.matches('.active-facets__button-remove')) {
        e.preventDefault();
        this.handleClearButton(e.target);
      }
    });

    // 3. Price Range Sliders (Custom Logic)
    document.addEventListener('price-range:change', (e) => {
       this.handlePriceChange(e.detail);
    });
  },

  resetPaginationParam() {
    // When filters/sort change, always return to first page.
    // Otherwise page=N can become empty after narrowing results.
    this.state.currentParams.delete('page');
  },

  handleInputChange(e) {
    const input = e.target;
    const name = input.name;
    const value = input.value;
    const isChecked = input.checked;

    console.log('🔍 handleInputChange called:', { name, value, isChecked, type: input.type });

    if (!name) {
      console.warn('⚠️ Input has no name attribute, skipping');
      return;
    }

    // Update params
    if (input.type === 'checkbox') {
      if (isChecked) {
        this.state.currentParams.append(name, value);
        console.log('✅ Added param:', name, '=', value);
      } else {
        const newParams = new URLSearchParams();
        for (const [key, val] of this.state.currentParams.entries()) {
          if (key === name && val === value) continue;
          newParams.append(key, val);
        }
        this.state.currentParams = newParams;
        console.log('❌ Removed param:', name, '=', value);
      }
    } else {
      // For text/number/range inputs, we set the value
      // If value is empty, we should delete the param?
      if (value === '') {
        this.state.currentParams.delete(name);
      } else {
        this.state.currentParams.set(name, value);
      }
    }

    this.resetPaginationParam();
    console.log('📋 Current params:', this.state.currentParams.toString());
    this.scheduleUpdate();
  },

  handleSortChange(e) {
    const value = e.target.value;
    this.state.currentParams.set('sort_by', value);
    this.resetPaginationParam();
    this.scheduleUpdate();
  },

  handleCardClick(card) {
    const tag = card.dataset.tag.toLowerCase();
    const activeTags = this.state.getActiveTags();
    const isActive = activeTags.includes(tag);
    const baseUrl = this.state.getCollectionBaseUrl();

    let newPath;

    // Mutual Exclusivity: 
    // If clicking active tag -> remove it (return to base)
    // If clicking inactive tag -> replace all tags with this one
    if (isActive) {
        newPath = baseUrl;
    } else {
        newPath = `${baseUrl}/${tag}`;
    }

    // Reset query filters when switching card categories.
    // Keep sort only so UX stays consistent.
    const sortBy = this.state.currentParams.get('sort_by');
    const newParams = new URLSearchParams();
    if (sortBy) newParams.set('sort_by', sortBy);

    this.state.currentParams = newParams;
    const queryString = newParams.toString();
    const newUrl = newPath + (queryString ? `?${queryString}` : '');

    // Keep collection interactions dynamic (AJAX update + history state)
    this.fetchProducts(newUrl, true);
  },

  handleClearButton(button) {
    const wrapper = button.closest('.lh-dropdown');
    if (wrapper) {
      const inputs = wrapper.querySelectorAll('input');
      const paramsToRemove = new Set();
      inputs.forEach(input => paramsToRemove.add(input.name));
      
      const newParams = new URLSearchParams();
      for (const [key, val] of this.state.currentParams.entries()) {
        if (!paramsToRemove.has(key)) {
          newParams.append(key, val);
        }
      }
      this.state.currentParams = newParams;
      this.resetPaginationParam();
      this.scheduleUpdate();
    }
  },

  handlePriceChange(detail) {
      if(detail.minName) this.state.currentParams.set(detail.minName, detail.minValue);
      if(detail.maxName) this.state.currentParams.set(detail.maxName, detail.maxValue);
      this.resetPaginationParam();
      this.scheduleUpdate();
  },

  handlePaginationClick(link) {
    const href = link && link.getAttribute('href');
    if (!href) return;

    const nextUrl = new URL(href, window.location.origin);
    const relativeUrl = nextUrl.pathname + nextUrl.search;

    // Keep internal state aligned with the clicked page query params.
    this.state.currentParams = new URLSearchParams(nextUrl.search);

    this.fetchProducts(relativeUrl, true);
  },

  hijackDrawerInputs() {
    const drawer = document.querySelector(this.config.drawerSelector);
    if (!drawer) return;
    
    const inputs = drawer.querySelectorAll('input:not([data-hijacked]):not(.js-input-min):not(.js-input-max)');
    
    inputs.forEach(input => {
        const clone = input.cloneNode(true);
        clone.setAttribute('data-hijacked', 'true');
        input.parentNode.replaceChild(clone, input);
    });
  },

  scheduleUpdate() {
    if (this.updateTimer) clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => {
      this.updateCollection();
    }, 300);
  },

  updateCollection() {
    const path = window.location.pathname; 
    const queryString = this.state.currentParams.toString();
    const url = path + (queryString ? `?${queryString}` : '');
    
    this.fetchProducts(url, true);
  },

  fetchProducts(url, updateHistory) {
    if (this.state.isLoading) return;
    this.state.isLoading = true;

    const grid = document.querySelector(this.config.gridContainerSelector);
    if (grid) grid.style.opacity = '0.4';

    if (updateHistory) {
      window.history.pushState(null, '', url);
    }

    fetch(url)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(html, 'text/html');
        this.renderProducts(newDoc);
        this.state.isLoading = false;
        if (grid) grid.style.opacity = '1';
      })
      .catch(err => {
        console.error('[FilterManager] Error:', err);
        this.state.isLoading = false;
        if (grid) grid.style.opacity = '1';
      });
  },

  renderProducts(newDoc) {
    // 1. Update Product Grid
    const newGrid = newDoc.querySelector(this.config.gridContainerSelector);
    const oldGrid = document.querySelector(this.config.gridContainerSelector);
    if (newGrid && oldGrid) {
      oldGrid.innerHTML = newGrid.innerHTML;
    }

    // 2. Update Desktop Filters
    const newDesktopFilters = newDoc.querySelector(this.config.desktopFilterContainer);
    const oldDesktopFilters = document.querySelector(this.config.desktopFilterContainer);
    if (newDesktopFilters && oldDesktopFilters) {
      oldDesktopFilters.innerHTML = newDesktopFilters.innerHTML;
    }
    
    // 3. Update Result Count (from hidden element that has paginate.items)
    this.updateResultCount(newDoc);
    
    // 4. Update Mobile Drawer Filters (Save scroll position before replacing)
    const drawerContainer = document.querySelector('#FilterDrawer');
    let savedScrollPosition = 0;
    
    if (drawerContainer) {
        const newFilterDrawer = newDoc.querySelector('#FilterDrawer');
        let newDrawerContent = newFilterDrawer ? newFilterDrawer.querySelector('.drawer__scrollable') : null;
        const oldDrawerContent = drawerContainer.querySelector('.drawer__scrollable');
        
        // Verify the new content is actually filter content, not nav menu
        if (newDrawerContent) {
          const hasNavMenu = newDrawerContent.innerHTML.includes('mobile-nav');
          const hasFilters = newDrawerContent.innerHTML.includes('filter-form') || 
                            newDrawerContent.innerHTML.includes('collection-sidebar');
          
          if (hasNavMenu && !hasFilters) {
            newDrawerContent = null; // Prevent corrupting the drawer
          }
        }
        
        if (newDrawerContent && oldDrawerContent) {
            savedScrollPosition = oldDrawerContent.scrollTop || 0;
            oldDrawerContent.innerHTML = newDrawerContent.innerHTML;
        }
    }
    
    // Store drawer reference for later scroll restoration
    const drawerContainerForScroll = drawerContainer;

    // 5. Ensure drawer button still works (protect immediately after DOM update)
    this.ensureDrawerButton();
    
    // 5b. Re-protect button with delays (theme might re-scan after our initial protection)
    // This is critical for card filter clicks where theme re-initializes drawers
    setTimeout(() => this.ensureDrawerButton(), 50);
    setTimeout(() => this.ensureDrawerButton(), 150);
    setTimeout(() => this.ensureDrawerButton(), 300);
    setTimeout(() => this.ensureDrawerButton(), 600);
    
    // 6. Ensure price range filter group is visible (might be hidden by Shopify's tag_count == 0 logic)
    this.ensurePriceRangeVisible();

    // 7. Hijack drawer inputs (before restoring values, exclude price inputs)
    this.hijackDrawerInputs();

    // 8. Sync UI (set input values before slider initialization)
    this.syncUI();

    // 8. Force synchronous update of price inputs
    this.forceUpdatePriceInputs();

    // 9. Re-init widgets (price sliders, quickshop, etc.)
    this.reinitWidgets();

    // 10. Restore drawer scroll position with delay
    if (savedScrollPosition > 0 && drawerContainerForScroll) {
        setTimeout(() => {
            const drawerContent = drawerContainerForScroll.querySelector(this.config.mobileFilterContainer);
            if (drawerContent) {
                drawerContent.scrollTop = savedScrollPosition;
                
                requestAnimationFrame(() => {
                    drawerContent.scrollTop = savedScrollPosition;
                    
                    setTimeout(() => {
                        if (Math.abs(drawerContent.scrollTop - savedScrollPosition) > 5) {
                             drawerContent.scrollTop = savedScrollPosition;
                        }
                    }, 100);
                });
            }
        }, 300);
    }
  },

  forceUpdatePriceInputs() {
      const priceInputs = document.querySelectorAll('.js-input-min, .js-input-max');
      priceInputs.forEach(input => {
          const name = input.name;
          if (name && this.state.currentParams.has(name)) {
              const val = this.state.currentParams.get(name);
              input.value = val;
              input.setAttribute('value', val);
          }
      });
  },

  updateResultCount(newDoc) {
    try {
      const newHiddenCount = newDoc.querySelector('#CollectionFilteredCount');
      
      if (newHiddenCount) {
        const count = newHiddenCount.textContent.trim();
        document.querySelectorAll('.lh-count-text').forEach(el => {
          el.textContent = `${count} résultats`;
        });
      }
    } catch (e) {
      console.error('[FilterManager] Error updating result count:', e);
    }
  },

  ensureDrawerButton() {
    const triggerBtn = document.querySelector('#FilterDrawerTrigger');
    if (!triggerBtn) return;
    
    triggerBtn.dataset.filterManagerInit = '';
    this.initDrawerButton();
  },

  ensurePriceRangeVisible() {
    const allFilterGroups = document.querySelectorAll('[class*="collection-sidebar__group--"]');
    
    allFilterGroups.forEach(group => {
      const hasPriceRange = group.querySelector('.price-range');
      
      if (hasPriceRange) {
        if (group.style.display === 'none' || window.getComputedStyle(group).display === 'none') {
          group.style.display = 'block';
        }
        
        const collapsibleContent = group.querySelector('.collapsible-content');
        if (collapsibleContent && !collapsibleContent.classList.contains('is-open')) {
          collapsibleContent.classList.add('is-open');
          collapsibleContent.style.height = 'auto';
        }
      }
    });
  },

  initDrawerButton() {
    let triggerBtn = document.querySelector('#FilterDrawerTrigger');
    
    if (!triggerBtn) return;

    if (triggerBtn.dataset.filterManagerInit === 'true') return;

    // STEP 1: Clone button to strip ALL existing event listeners
    const newBtn = triggerBtn.cloneNode(true);
    triggerBtn.parentNode.replaceChild(newBtn, triggerBtn);
    triggerBtn = newBtn;
    
    // STEP 2: Remove ALL theme drawer classes
    triggerBtn.classList.remove('js-drawer-open-collection-filters');
    triggerBtn.classList.remove('js-drawer-open-left');
    triggerBtn.classList.remove('js-drawer-open-right');
    triggerBtn.classList.remove('js-drawer-open');
    
    // STEP 3: Ensure the drawer instance exists
    if (typeof theme !== 'undefined' && theme.Drawers) {
      const drawerEl = document.querySelector('#FilterDrawer');
      if (drawerEl && !theme.FilterDrawer) {
        theme.FilterDrawer = new theme.Drawers('FilterDrawer', 'collection-filters');
      }
    }
    
    // STEP 4: Add click handler in CAPTURE phase
    triggerBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      if (typeof theme !== 'undefined' && theme.FilterDrawer) {
        if (theme.NavDrawer && theme.NavDrawer.isOpen) {
          theme.NavDrawer.close();
        }
        if (theme.CartDrawer && theme.CartDrawer.isOpen) {
          theme.CartDrawer.close();
        }
        
        theme.FilterDrawer.open();
      } else {
        if (typeof theme !== 'undefined' && theme.Drawers) {
          const drawerEl = document.querySelector('#FilterDrawer');
          if (drawerEl) {
            theme.FilterDrawer = new theme.Drawers('FilterDrawer', 'collection-filters');
            theme.FilterDrawer.open();
          }
        }
      }
      
      return false;
    }, true);
    
    triggerBtn.dataset.filterManagerInit = 'true';
  },

  syncUI() {
    // 1. Sync Cards (Path-based tags)
    const activeTags = this.state.getActiveTags();
    const cards = document.querySelectorAll(this.config.cardSelector);
    cards.forEach(card => {
        const tag = card.dataset.tag.toLowerCase();
        if (activeTags.includes(tag)) {
            card.classList.add('is-active');
        } else {
            card.classList.remove('is-active');
        }
    });

    // 2. Sync Checkboxes (Query Params)
    const checkboxes = document.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(input => {
        const name = input.name;
        const value = input.value;
        const shouldBeChecked = this.state.currentParams.getAll(name).includes(value);
        if (input.checked !== shouldBeChecked) {
            input.checked = shouldBeChecked;
        }
    });

    // 3. Sync Number/Text Inputs (e.g. Price Range)
    const textInputs = document.querySelectorAll('input[type="number"], input[type="text"]');
    textInputs.forEach(input => {
        const name = input.name;
        if (name && this.state.currentParams.has(name)) {
            const val = this.state.currentParams.get(name);
            if (input.value !== val) {
                input.value = val;
                input.setAttribute('value', val);
            } else {
                input.setAttribute('value', val);
            }
        }
    });
    
    // 4. Sync SortBy
    const sortBy = this.state.currentParams.get('sort_by');
    if (sortBy) {
        const sortSelect = document.querySelector('#SortBy');
        if (sortSelect) sortSelect.value = sortBy;
    }
  },

  reinitWidgets() {
    if (window.initCustomFilters) {
        window.initCustomFilters();
    }
    if (typeof theme !== 'undefined') {
        if (typeof theme.reinitProductGridItem === 'function') theme.reinitProductGridItem();
        if (typeof theme.initQuickShop === 'function') theme.initQuickShop();
    }
    if (window.SPR) {
        SPR.initDomEls();
        SPR.loadBadges();
    }
    // Re-initialize product card tooltips after AJAX update
    // File: assets/collection-filter-manager.js
    if (window.initProductCardTooltips) {
        const grid = document.querySelector(this.config.gridContainerSelector);
        if (grid) {
            window.initProductCardTooltips(grid);
        }
    }
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => CollectionFilterManager.init());
} else {
  CollectionFilterManager.init();
}

window.CollectionFilterManager = CollectionFilterManager;
